import { useRef, useState, type ChangeEvent } from 'react';
import { Download, Upload } from 'lucide-react';
import { backupCounts, validateBackup, type BackupCounts } from '@muddle/core';
import { api } from '../api';
import { ConfirmDialog, ErrorMessage, Skeleton, useToast } from '../components';
import { saveFromApi, today } from '../fileSave';
import { useAsync } from '../hooks/useAsync';
import { formatDate } from '../format';

const KIND_LABEL = {
  auto: 'Automatic',
  manual: 'Manual',
  prerestore: 'Before a restore',
  nightly: 'Nightly',
} as const;

const size = (bytes: number) =>
  bytes > 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
const countsText = (c: BackupCounts) =>
  `${c.cocktails} drink${c.cocktails === 1 ? '' : 's'}, ${c.versions} version${c.versions === 1 ? '' : 's'}, ${c.tastings} tasting${c.tastings === 1 ? '' : 's'}, ${c.ingredients} ingredient${c.ingredients === 1 ? '' : 's'}`;

type Pending =
  | { kind: 'json'; fileName: string; data: Parameters<typeof api.importJson>[0] }
  | { kind: 'database'; fileName: string; file: File }
  | { kind: 'snapshot'; name: string; when: string };

/** Export, snapshots and restore. Everything destructive goes through a confirm dialog. */
export function BackupCards() {
  const toast = useToast();
  const [backups, reload] = useAsync(api.listBackups);
  const [busy, setBusy] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [fileError, setFileError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const guard = async (label: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(label);
    try {
      await fn();
      if (done) toast(done);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Something went wrong.', { error: true });
    } finally {
      setBusy(null);
    }
  };

  const exportFile = (label: string, apiPath: string, name: string) =>
    guard(label, async () => {
      const r = await saveFromApi(apiPath, name);
      if (r.saved) toast('Export saved');
    });

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow choosing the same file again
    setFileError('');
    if (!file) return;
    if (/\.sqlite$/i.test(file.name)) {
      setPending({ kind: 'database', fileName: file.name, file });
      return;
    }
    try {
      const { data, errors } = validateBackup(JSON.parse(await file.text()));
      if (!data) {
        setFileError(errors.join(' '));
        return;
      }
      setPending({ kind: 'json', fileName: file.name, data });
    } catch {
      setFileError(
        "That file isn't a Muddle backup. Choose a .json backup or a .sqlite database export.",
      );
    }
  };

  const confirmRestore = () => {
    const p = pending;
    setPending(null);
    if (!p) return;
    void guard(
      'restore',
      async () => {
        if (p.kind === 'json') await api.importJson(p.data);
        else if (p.kind === 'database') await api.importDatabase(p.file);
        else await api.restoreBackup(p.name);
        reload();
      },
      'Restored. A safety backup of your previous data was saved first.',
    );
  };

  return (
    <>
      <section className="card" aria-labelledby="exp-title">
        <h2 className="card-title" id="exp-title">
          Export
        </h2>
        <p className="card-sub">
          Take your recipes anywhere. Archived drinks are left out of the Excel file.
        </p>
        <div className="list-row">
          <div className="grow">
            <div className="row-title">Excel workbook</div>
            <div className="row-meta">
              Drinks, recipe lines, version history and tastings (.xlsx)
            </div>
          </div>
          <button
            className="btn btn-sm"
            type="button"
            disabled={busy !== null}
            onClick={() =>
              void exportFile('xlsx', '/export/xlsx', `muddle-cocktails-${today()}.xlsx`)
            }
          >
            <Download size={14} aria-hidden /> {busy === 'xlsx' ? 'Exporting…' : 'Export'}
          </button>
        </div>
        <div className="list-row">
          <div className="grow">
            <div className="row-title">Backup file</div>
            <div className="row-meta">
              Everything, including archived items (.json). Restorable here.
            </div>
          </div>
          <button
            className="btn btn-sm"
            type="button"
            disabled={busy !== null}
            onClick={() => void exportFile('json', '/export/json', `muddle-backup-${today()}.json`)}
          >
            <Download size={14} aria-hidden /> {busy === 'json' ? 'Exporting…' : 'Export'}
          </button>
        </div>
        <div className="list-row">
          <div className="grow">
            <div className="row-title">Full database</div>
            <div className="row-meta">A copy of the SQLite file (.sqlite)</div>
          </div>
          <button
            className="btn btn-sm"
            type="button"
            disabled={busy !== null}
            onClick={() =>
              void exportFile('db', '/export/database', `muddle-database-${today()}.sqlite`)
            }
          >
            <Download size={14} aria-hidden /> {busy === 'db' ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </section>

      <section className="card" aria-labelledby="bk-title">
        <div className="mu-card-head">
          <h2 className="card-title" id="bk-title">
            Backups
          </h2>
          <button
            className="btn btn-sm"
            type="button"
            disabled={busy !== null}
            aria-busy={busy === 'now'}
            onClick={() =>
              void guard(
                'now',
                async () => {
                  await api.createBackup();
                  reload();
                },
                'Backup created',
              )
            }
          >
            {busy === 'now' ? 'Backing up…' : 'Back up now'}
          </button>
        </div>
        <p className="card-sub">
          A backup is saved automatically when Muddle closes. The last 10 are kept.
        </p>
        {backups.status === 'loading' && <Skeleton height={48} />}
        {backups.status === 'error' && (
          <ErrorMessage message={`Couldn't load backups. ${backups.message}`} onRetry={reload} />
        )}
        {backups.status === 'ready' && backups.data.length === 0 && (
          <div className="empty">
            <div className="empty-title">No backups yet</div>
            The first one appears when you close the app, or press Back up now.
          </div>
        )}
        {backups.status === 'ready' &&
          backups.data.map((b) => (
            <div key={b.name} className="list-row">
              <div className="grow">
                <div className="row-title">
                  {formatDate(b.createdAt)}{' '}
                  <span className="muted num">
                    {new Date(b.createdAt).toLocaleTimeString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <div className="row-meta">
                  {KIND_LABEL[b.kind]} · <span className="num">{size(b.size)}</span>
                </div>
              </div>
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                disabled={busy !== null}
                onClick={() =>
                  setPending({ kind: 'snapshot', name: b.name, when: formatDate(b.createdAt) })
                }
              >
                Restore
              </button>
            </div>
          ))}
      </section>

      <section className="card" aria-labelledby="imp-title">
        <h2 className="card-title" id="imp-title">
          Restore from a file
        </h2>
        <p className="card-sub">
          Choose a backup (.json) or database export (.sqlite). This replaces your current data.
        </p>
        <input
          ref={fileInput}
          type="file"
          accept=".json,.sqlite,application/json"
          className="sr-only"
          id="restore-file"
          aria-label="Choose a backup or database file to restore"
          onChange={(e) => void onFile(e)}
        />
        <div className="actions">
          <button
            className="btn btn-ghost"
            type="button"
            disabled={busy !== null}
            onClick={() => fileInput.current?.click()}
          >
            <Upload size={16} aria-hidden /> Choose file…
          </button>
        </div>
        {fileError && (
          <div className="msg err" role="alert">
            {fileError}
          </div>
        )}
      </section>

      <ConfirmDialog
        open={pending !== null}
        title="Replace all your data?"
        body={
          pending?.kind === 'json'
            ? `${pending.fileName} contains ${countsText(backupCounts(pending.data))}. Everything you have now will be replaced. A safety backup of your current data is saved first.`
            : pending?.kind === 'database'
              ? `Everything you have now will be replaced by the contents of ${pending.fileName}. A safety backup of your current data is saved first.`
              : pending?.kind === 'snapshot'
                ? `Everything you have now will be replaced by the backup from ${pending.when}. A safety backup of your current data is saved first.`
                : ''
        }
        confirmLabel="Replace my data"
        onCancel={() => setPending(null)}
        onConfirm={confirmRestore}
      />
    </>
  );
}
